<template>
  <div
    class="bb-table-wrapper"
    id="board"
  >
    <vue-good-table
      :columns="headings"
      :fixed-header="false"
      :rows="skills"
      :pagination-options="{
        enabled: true,
        mode: 'records',
        perPage: 8,
        position: 'bottom',
        dropdownAllowAll: true,
        setCurrentPage: 1,
        nextLabel: 'next',
        prevLabel: 'prev',
        rowsPerPageLabel: 'Skills per page',
        ofLabel: 'of',
        allLabel: 'All',
      }"
      :search-options="{ enabled: true, placeholder: 'Search...' }"
      :row-style-class="rowStyleClassFn"
      v-on:cell-click="onCellClick"
      styleClass="vgt-table bordered"
      theme="black-rhino"
    >
      <template v-slot:table-row="props">
        <confirm-dialog ref="confirmDialog"></confirm-dialog>

        <!-- no heading is blank label -->
        <span v-if="props.column.label == ''"> </span>

        <span v-if="props.column.field === 'usable_image'">
          <ToolTip
            v-if="isWeaponUsabilityWarning(props.row)"
            tableWrapperMode
            position="top"
            :text="props.row.unusable_reasons"
          >
            <span class="bb-usable-icon-wrapper">
              <img
                :src="getStatusImage(props)"
                class="bb-img-visible"
                height="50"
                width="50"
                alt="weapon skill usability"
              />
              <span class="bb-orange-circle"></span>
            </span>
          </ToolTip>
          <img
            v-else
            :src="getStatusImage(props)"
            class="bb-img-visible"
            height="50"
            width="50"
            alt="weapon skill usability"
          />
        </span>

        <span
          v-else-if="
            props.column.field === 'capped_ticks_image' ||
            props.column.field === 'capped_skill_image' ||
            props.column.field === 'integrated_rune_image' ||
            props.column.field === 'favourite_image'
          "
          @click.stop="
            props.column.field === 'favourite_image'
              ? toggleFavourite(props.row, $event)
              : null
          "
        >
          <img
            :src="getStatusImage(props)"
            :class="[
              isVisible(props),
              props.column.field === 'favourite_image' ? 'bbActionIcon' : '',
            ]"
            height="50"
            width="50"
            alt="maxed image"
          />
        </span>

        <!-- row number -->
        <span v-if="props.column.field === 'rowNumber'">
          {{
            props.row.originalIndex !== undefined
              ? props.row.originalIndex + 1
              : props.index + 1
          }}
        </span>

        <span v-if="shouldShowCell(props)">
          <ToolTip
            v-if="getToolTipData(props)"
            tableWrapperMode
            position="top"
          >
            <template #content>
              <div class="tooltip-bold">{{ getToolTipData(props).header }}</div>
              <div style="white-space: pre-line">
                {{ getToolTipData(props).body }}
              </div>
            </template>
            <span
              class="bb-inline-cell"
              :style="
                props.column.field === 'cur'
                  ? 'font-weight: bold;font-size:1.1em;'
                  : ''
              "
            >
              {{ props.formattedRow[props.column.field] }}
              <span class="bb-orange-circle"></span>
            </span>
          </ToolTip>
          <span v-else>
            <span>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </span>
        </span>

        <!-- DELETE -->
        <span v-if="props.column.field === 'action_delete'">
          <img
            class="bbActionIcon"
            :src="deleteImg"
            @click="confirmDeletion(props.row, $event)"
            height="50"
            width="50"
            alt="delete image"
          />
        </span>

        <!-- APPLIED ADJ ADJUST -->
        <span v-if="props.column.field === 'action_adjust'">
          <template v-if="isActiveSkill(props)">
            <img
              class="bbActionIcon"
              :src="adjustImg"
              @click="adjustSkill(props.row)"
              height="50"
              width="50"
              alt="adjust image"
            />
          </template>
        </span>

        <!-- OPTIONAL ADJ ADJUST -->
        <span v-if="props.column.field === 'action_adjust_opt'">
          <template v-if="isActiveSkill(props)">
            <img
              class="bbActionIcon"
              :src="adjustImg"
              @click="optionalAdjustSkill(props.row)"
              height="50"
              width="50"
              alt="adjust image"
            />
          </template>
        </span>
      </template>

      <template #emptystate
        >*** Character has no skills of this type ***</template
      >
    </vue-good-table>
  </div>
</template>

<script>
import 'vue-good-table-next/dist/vue-good-table-next.css'

import { VueGoodTable } from 'vue-good-table-next'
import ConfirmDialog from './ConfirmDialog.vue'
import { inject } from 'vue'

import ToolTip from '../components/ToolTip.vue'

import getPopulatedSkillCurNoAdjs from '../helpers/getPopulatedSkillCurNoAdjs'
import getPopulatedSkillMaxTicks from '../helpers/getPopulatedSkillMaxTicks'
import getPopulatedSkillIncrease from '../helpers/getPopulatedSkillIncrease'
import getPopulatedSkillCurrent from '../helpers/getPopulatedSkillCurrent'
import getPopulatedSkillBase from '../helpers/getPopulatedSkillBase'

import scrollTable from '../helpers/scrollTable'
import isEmpty from '../helpers/isEmpty'

import adjustImg from '../assets/images/bbAdjust.png'
import deleteImg from '../assets/images/bbDelete48.png'
import exclamationImg from '../assets/images/bbExclamation2.png'
import tickImg from '../assets/images/bbTick.png'
import crossImg from '../assets/images/bbBoxedCross.png'

export default {
  name: 'TableSkills',
  //==================================================================
  // components
  //------------
  components: {
    VueGoodTable,
    ConfirmDialog,
    ToolTip,
  },
  //==================================================================
  // setup
  setup() {
    const global = inject('global')
    return { global }
  },
  //==================================================================
  // props
  //-------
  props: {
    headings: {
      type: Array,
      required: true,
    },
    skill_type: {
      type: String,
      required: true,
    },
    skills: {
      type: Array,
      required: true,
    },
    character: {
      type: Object,
      required: true,
    },
    max_skill: {
      type: Number,
      required: true,
    },
  },
  emits: [
    'adjust-character-skill',
    'optional-adjust-character-skill',
    'show-character-skill',
    'delete-character-skill',
    'toggle-favourite',
  ],
  //==================================================================
  // data
  //------
  data() {
    return {
      selectedValue: null,
      selectedColumn: null,
      selectedSkill: null,
      isDeletingClearTimer: null,

      //
      skills_array: [],
      //
      adjustImg,
      deleteImg,
      exclamationImg,
      tickImg,
      crossImg,
      //
      isDeleting: false,
    }
  },
  //====================================================================
  // methods
  //---------
  methods: {
    isWeaponSkillType() {
      return this.skill_type === 'Melee' || this.skill_type === 'Ranged'
    },
    isWeaponSkillRowInactive(row) {
      if (!this.isWeaponSkillType()) {
        return false
      }

      return row?.usable_status && row.usable_status !== 'Yes'
    },
    isWeaponUsabilityWarning(row) {
      return (
        this.isWeaponSkillRowInactive(row) &&
        row?.unusable_reasons &&
        row.unusable_reasons !== 'n/a'
      )
    },
    hasIntegratedBackingRune(row) {
      if (!row || row?.name !== 'Runecasting') {
        return true
      }

      const runeFamilyName = row?.display_name || row?.name || ''
      if (!runeFamilyName) {
        return false
      }

      const runes = Array.isArray(this.character?.runes)
        ? this.character.runes
        : []

      return runes.some(
        (rune) => rune?.name === runeFamilyName && rune?.integrated === true,
      )
    },
    hasBackingRuneFamily(row) {
      if (!row || row?.name !== 'Runecasting') {
        return true
      }

      const runeFamilyName = row?.display_name || row?.name || ''
      if (!runeFamilyName) {
        return false
      }

      const runes = Array.isArray(this.character?.runes)
        ? this.character.runes
        : []

      return runes.some((rune) => rune?.name === runeFamilyName)
    },
    //==================================================================
    // is inactive runecasting row
    //---------------------------
    isInactiveRunecastingRow(props) {
      return (
        this.skill_type === 'Casting' &&
        props.row['name'] === 'Runecasting' &&
        !this.hasIntegratedBackingRune(props.row)
      )
    },
    //==================================================================
    // is active skill
    //-----------------
    isActiveSkill(props) {
      if (this.isWeaponSkillRowInactive(props.row)) {
        return false
      }

      if (props.row?.name === 'Runecasting') {
        return this.hasIntegratedBackingRune(props.row)
      }

      if (props.row['integrated'] === false && this.skill_type === 'Rune') {
        return false
      }
      return true
    },
    //==================================================================
    // get status image
    //-----------------
    getStatusImage(props) {
      if (props.column.field === 'usable_image') {
        if (props.row?.usable_status === 'No') {
          return this.crossImg
        }

        if (props.row?.usable_status === 'Partial') {
          return this.exclamationImg
        }

        return this.tickImg
      }

      if (
        props.column.field === 'integrated_rune_image' &&
        this.isInactiveRunecastingRow(props)
      ) {
        return this.hasBackingRuneFamily(props.row)
          ? this.crossImg
          : this.exclamationImg
      }

      return this.tickImg
    },
    //==================================================================
    // adjust skill (emits event to launch adjusts panel)
    //--------------
    adjustSkill(row) {
      // console.log('adjustSkill...');

      let adj_type = ''
      const name = row['name'] || ''
      const display_name = row['display_name'] || ''
      // console.log('adj? display_name=', display_name);
      // ----------------------------------------------------------------------------------------------------------
      // where runecasting skill:
      // - use display name as want adj vs rune name (not global runecasting skill like magic rune)
      // - shouldn't need runecasting check as display_name limited to rune skills for now but included for safety
      // ----------------------------------------------------------------------------------------------------------
      if (name == 'Runecasting' && !isEmpty(display_name)) {
        console.log('adjustSkill for display_name=', display_name)
        adj_type = display_name
      } else {
        console.log('adjustSkill for name=', name)
        adj_type = name
      }

      this.$emit('adjust-character-skill', {
        adj_category: 'skill',
        adj_type: adj_type,
      })
    },
    //==================================================================
    // optional adjust skill (emits event to launch opt adjusts panel)
    //-----------------------
    optionalAdjustSkill(row) {
      // console.log('adjustSkill...');

      let adj_type = ''
      const name = row['name'] || ''
      const display_name = row['display_name'] || ''
      // console.log('adj? display_name=', display_name);
      // ----------------------------------------------------------------------------------------------------------
      // where runecasting skill:
      // - use display name as want adj vs rune name (not global runecasting skill like magic rune)
      // - shouldn't need runecasting check as display_name limited to rune skills for now but included for safety
      // ----------------------------------------------------------------------------------------------------------
      if (name == 'Runecasting' && !isEmpty(display_name)) {
        console.log('adjustSkill for display_name=', display_name)
        adj_type = display_name
      } else {
        console.log('adjustSkill for name=', name)
        adj_type = name
      }

      this.$emit('optional-adjust-character-skill', {
        adj_category: 'skill',
        adj_type: adj_type,
      })
    },
    toggleFavourite(row, event) {
      if (event && typeof event.stopPropagation === 'function') {
        event.stopPropagation()
      }

      this.$emit('toggle-favourite', row)
    },
    //==================================================================
    // show show cell
    //----------------
    shouldShowCell(props) {
      const iconOnlyFields = [
        'capped_ticks_image',
        'capped_skill_image',
        'integrated_rune_image',
        'favourite_image',
        'usable_image',
      ]

      if (iconOnlyFields.includes(props.column.field)) {
        return false
      }

      // ------------
      // adjs fields
      // ------------
      const zeroFields = [
        'adjs_tot_character_skill',
        'adjs_tot_integrated_rune_skill',
        'adjs_tot_characteristic_skill',
      ]
      // ------------
      // adjs checks
      // ------------
      if (zeroFields.includes(props.column.field)) {
        const value = props.row[props.column.field]
        // build corresponding reason field name
        const reasonField = props.column.field.replace(
          'adjs_tot_',
          'adjs_reasons_',
        )
        const reason = props.row[reasonField]
        // hide if value is 0 and reason is blank/undefined/null
        if (
          (value === 0 || value === '0') &&
          (!reason || reason.trim() === '')
        ) {
          return false
        }
      }
      // --------------------
      // parent check fields
      // --------------------
      const zeroFields_parent = ['training', 'ticks']
      // --------------
      // parent checks
      // -------------
      if (zeroFields_parent.includes(props.column.field)) {
        const value = props.row[props.column.field]
        // hide if value is 0 and parent exists
        if (
          (value === 0 || value === '0') &&
          !isEmpty(props.row['parent_name'])
        ) {
          return false
        }
      }
      return true
    },
    //==================================================================
    // get tooltip data
    //-------------
    getToolTipData(props) {
      const tooltipContext = props.row['name'] + ' ' + props.column.label + ': '

      const buildTooltip = (body) => ({
        header: tooltipContext,
        body: body ?? '',
      })

      // --------------
      // base skill
      // --------------
      if (props.column.field == 'base') {
        let args = {}
        // where child skill
        if (!isEmpty(props.row['parent_name'])) {
          args = {
            parent_name: props.row['parent_name'],
            parent_base: props.row['base'],
          }
        } else {
          // pass std base calc fields
          args = {
            arg1: props.row['formula_arg1'],
            arg2: props.row['formula_arg2'],
            arg3: props.row['formula_arg3'],
            oper1: props.row['formula_oper1'],
            oper2: props.row['formula_oper2'],
          }
        }
        // get populated skill base
        const populated_base = getPopulatedSkillBase(args, this.character)
        return buildTooltip(populated_base)
        // --------------
        // increase
        // --------------
      } else if (
        props.column.field == 'incr_percent' &&
        isEmpty(props.row['parent_name'])
      ) {
        const populated_increase = getPopulatedSkillIncrease(
          props.row['cur_no_adjs'],
          this.character.know_bonus_percent,
        )
        return buildTooltip(populated_increase)
        // --------------
        // cur skill
        // --------------
      } else if (props.column.field == 'cur') {
        // get args
        const args = {
          ticks: props.row['ticks'],
          training: props.row['training'],
          adjs_tot: props.row['adjs_tot'],
        }

        // if child skill - pass parent_cur
        if (!isEmpty(props.row['parent_name'])) {
          args['parent_name'] = props.row['parent_name']
          args['parent_cur'] = props.row['parent_cur']
        } else {
          // else pass base
          args['base'] = props.row['base']
        }

        // get populated skill cur
        const populated_cur = getPopulatedSkillCurrent(args)
        return buildTooltip(populated_cur)
        // --------------
        // cur no adjs
        // --------------
      } else if (props.column.field == 'cur_no_adjs') {
        let args = {}

        // get args if child skill
        if (!isEmpty(props.row['parent_name'])) {
          args = {
            parent_name: props.row['parent_name'],
            parent_cur_no_adjs: props.row['parent_cur_no_adjs'],
          }
        } else {
          // pass std cur no adjs fields
          args = {
            base: props.row['base'],
            ticks: props.row['ticks'],
            training: props.row['training'],
          }
        }
        // get populated skill cur
        const populated_cur_no_adjs = getPopulatedSkillCurNoAdjs(args)
        return buildTooltip(populated_cur_no_adjs)
        // ----------
        // max ticks
        // ----------
      } else if (
        props.column.field == 'ticks_max' &&
        isEmpty(props.row['parent_name'])
      ) {
        // get args
        const args = {
          base: props.row['base'],
          training: props.row['training'],
          max_skill: this.max_skill,
          cur_no_adjs: props.row['cur_no_adjs'],
        }
        // get populated skill cur
        const populated_ticks_max = getPopulatedSkillMaxTicks(args)
        return buildTooltip(populated_ticks_max)
      }
      return null
    },
    //==================================================================
    // is visible
    //------------
    isVisible(props) {
      if (
        props.column.field == 'integrated_rune_image' &&
        this.isInactiveRunecastingRow(props)
      ) {
        return 'bb-img-visible'
      }

      if (props.column.field == 'favourite_image') {
        return props.row['favourite'] == true
          ? 'bb-img-visible'
          : 'bb-img-hidden'
      }

      if (
        (props.column.field == 'capped_ticks_image' &&
          props.row['capped_ticks'] == true) ||
        (props.column.field == 'capped_skill_image' &&
          props.row['capped_skill'] == true) ||
        (props.column.field == 'integrated_rune_image' &&
          // on the shared 'Casting' tab this column only applies to the
          // dedicated Rune casting skill row, never Sorcery/Divine rows
          (this.skill_type !== 'Casting' || props.row?.name === 'Runecasting') &&
          this.isActiveSkill(props))
      ) {
        return 'bb-img-visible'
      }
      return 'bb-img-hidden'
    },
    //==================================================================
    // on cell click - open skill sheet
    //----------------------------------
    onCellClick(params) {
      // --------------------------------------------------------------
      // prevent cell click if deleting or clicking from action column
      // --------------------------------------------------------------
      if (this.isDeleting || params.column.field.substring(0, 6) == 'action') {
        return
      }

      if (params.column.field === 'favourite_image') {
        this.toggleFavourite(params.row)
        return
      }

      // ---------------------------
      // show character skill panel
      // ---------------------------
      this.$emit('show-character-skill', params.row)
    },
    //==================================================================
    // colour rows
    //-------------
    rowStyleClassFn(row) {
      if (this.isWeaponSkillRowInactive(row)) {
        return 'rowInactive'
      }

      if (this.skill_type === 'Rune' && row['integrated'] === false) {
        return 'rowInactive'
      }

      if (this.skill_type === 'Casting' && row['name'] === 'Runecasting') {
        if (!this.hasIntegratedBackingRune(row)) {
          return 'rowInactive'
        }
      }

      if (row.originalIndex % 2 === 0) {
        return 'rowNeutral'
      } else {
        return 'rowParty'
      }
    },
    //==================================================================
    // handle scroll table
    //---------------------
    handleScrollTable(dir) {
      // console.log("TableSkills handleScrollTable", dir);
      scrollTable(document, dir)
    },
    //==================================================================
    // confirm deletion
    //------------------
    async confirmDeletion(row) {
      // -----------------------------------
      // prevent cell click whilst deleting
      // -----------------------------------
      this.isDeleting = true
      // --------------------
      // show confirm dialog
      // --------------------
      try {
        const ok = await this.$refs.confirmDialog.show({
          title: 'Delete Character Skill',
          message: 'Confirm Deletion',
          subject: row['name'],
          okButton: 'OK',
          cancelButton: 'Cancel',
        })
        // -----------------------
        // ok - emit delete event
        // -----------------------
        if (ok) this.$emit('delete-character-skill', row) // pass skill
      } finally {
        // -------------------------
        // clear any existing timer
        // -------------------------
        if (this.isDeletingClearTimer) clearTimeout(this.isDeletingClearTimer)
        // ----------------------------------
        // delay clearing isDeleting (300ms)
        // ----------------------------------
        this.isDeletingClearTimer = setTimeout(() => {
          this.isDeleting = false
          this.isDeletingClearTimer = null
        }, 300)
      }
    },
  },
  //==================================================================
  // before unmount
  //----------------
  beforeUnmount() {
    // ------------------------
    // clear any pending timer
    // ------------------------
    if (this.isDeletingClearTimer) {
      clearTimeout(this.isDeletingClearTimer)
      this.isDeletingClearTimer = null
    }
  },
}
</script>
